import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, FormHelperText, TextField, FormControlLabel, Checkbox, Alert, Divider } from '@mui/material';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import Iconify from '../Iconify';
import * as Yup from 'yup';
import axios from 'axios';
import '../../css/companies/addCompanyDialog.css';

export const AddCompanyDialog = ({ setCompanies, setTotalCompanies, setFilteredCompaniesCount, openAddCompany, setOpenAddCompany, setCompanyId }) => {

    const { t } = useTranslation();
    const navigate = useNavigate();

    const [errMsg, setErrMsg] = useState('');

    const formik = useFormik({

        initialValues: {
            name: '',
            street_address: '',
            address_line_2: '',
            city: '',
            postal_code: '',
            can_manage_machine_control: false,
        },

        validationSchema: Yup.object().shape({
            name: Yup.string().required(t('isRequired')),
            street_address: Yup.string().optional(),
            address_line_2: Yup.string().optional(),
            city: Yup.string().optional(),
            postal_code: Yup.string().optional(),
            can_manage_machine_control: Yup.boolean().optional(),
        }),

        onSubmit: async (values) => {
            try {
                // Send the FormData object via Axios
                const response = await axios.post('company', values);

                if (response.status === 200 || response.status === 201) {
                    setOpenAddCompany(false);
                    setCompanyId(response?.data?.id);
                    setCompanies(prevCompanies => [...prevCompanies, response.data]);
                    setTotalCompanies(prevTotal => prevTotal + 1);
                    setFilteredCompaniesCount(prevCount => prevCount + 1);
                    navigate(`entreprise_details/${response.data.id}`);
                }

            } catch (error) {
                setErrMsg(error?.response?.data?.message || error.message || 'Erreur lors de l\'ajout de l\'entreprise');
                console.error('Error adding company:', error);
                CustomErrorLogger(error, 'Error adding company in AddCompanyDialog');
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, touched, errors } = formik;

    const handleClose = useCallback(() => setOpenAddCompany(false), [setOpenAddCompany]);

    return (
        <Dialog open={openAddCompany} fullWidth maxWidth="md">
            <IconButton onClick={handleClose} className='addCompany_icon_button'>
                <Iconify icon="material-symbols:close" className='addCompany_close_icon' />
            </IconButton>
            <DialogTitle className='addCompany_dialog_title'>Ajouter une entreprise</DialogTitle>
            <Divider />
            {errMsg && <Alert key={errMsg} severity="error">{errMsg}</Alert>}
            <form onSubmit={handleSubmit}>
                <DialogContent className='addCompany_dialog_content'>
                    <Box className="addCompany_helper_field_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addCompanyInputTitle">Nom de l’entreprise</FormHelperText>
                        <TextField
                            fullWidth
                            placeholder='Entrez le titre'
                            value={values.name}
                            className='addCompany_InputField'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                        />
                    </Box>
                    <Box className="addCompany_helper_field_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addCompanyInputTitle">Adresse (optionnel)</FormHelperText>
                        <TextField
                            fullWidth
                            placeholder='Entrez addresse'
                            value={values.street_address}
                            className='addCompany_InputField'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="street_address"
                            error={touched.street_address && Boolean(errors.street_address)}
                            helperText={touched.street_address && errors.street_address}
                        />
                    </Box>
                    <Box className="updateCompanyAddress_helper_input_box">
                        <FormHelperText id="outlined-weight-helper-text" className="updateCompanyAddressInputTitle">Complément d'adresse (optionnel)</FormHelperText>
                        <TextField
                            fullWidth
                            value={values.address_line_2}
                            className='updateCompanyAddress_InputField'
                            id="outlined-adornment-weight"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="address_line_2"
                            error={touched.address_line_2 && Boolean(errors.address_line_2)}
                            helperText={touched.address_line_2 && errors.address_line_2}
                            aria-describedby="outlined-weight-helper-text"
                        />
                    </Box>
                    <Box className="updateCompanyAddress_helper_input_box">
                        <FormHelperText id="outlined-weight-helper-text" className="updateCompanyAddressInputTitle">Ville (optionnel)</FormHelperText>
                        <TextField
                            fullWidth
                            value={values.city}
                            className='updateCompanyAddress_InputField'
                            id="outlined-adornment-weight"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="city"
                            error={touched.city && Boolean(errors.city)}
                            helperText={touched.city && errors.city}
                            aria-describedby="outlined-weight-helper-text"
                        />
                    </Box>
                    <Box className="updateCompanyAddress_helper_input_box">
                        <FormHelperText id="outlined-weight-helper-text" className="updateCompanyAddressInputTitle">Postal code (optionnel)</FormHelperText>
                        <TextField
                            fullWidth
                            value={values.postal_code}
                            className='updateCompanyAddress_InputField'
                            id="outlined-adornment-weight"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="postal_code"
                            error={touched.postal_code && Boolean(errors.postal_code)}
                            helperText={touched.postal_code && errors.postal_code}
                            aria-describedby="outlined-weight-helper-text"
                        />
                    </Box>
                    <Box className="updateCompanyAddress_helper_input_box">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={values.can_manage_machine_control}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    name="can_manage_machine_control"
                                    sx={{
                                        '&.Mui-checked': {
                                            color: '#EA5E00',
                                        },
                                    }}
                                />
                            }
                            label='Peut gérer le contrôle des machines'
                        />
                        {touched.can_manage_machine_control && errors.can_manage_machine_control && (
                            <FormHelperText error>{errors.can_manage_machine_control}</FormHelperText>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions className='addCompany_dialog_actions'>
                    <Button className='addCompany_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Button className='addCompany_button_valider' variant="contained" type="submit">{t('Valider')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};